import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import { authLink, safeReturnTo } from "@client/lib/returnTo";
import type { SessionUser } from "@shared/api";
import { ME_QUERY_KEY } from "@client/app/auth";
import { api } from "@client/api/client";
import { Button, Card, Input, Spinner } from "@client/components/ui";
import { useT, type TFunction } from "@client/i18n";
import { fieldErrors, isCode, messageForError, rateLimitRetrySeconds } from "@client/lib/errors";

/** `GET /api/auth/google/pending` (spec §2): what the signed `gpending` cookie holds. */
interface PendingSignup {
  returnTo?: string;
  email: string;
  registrationCodeRequired: boolean;
  teacherCodeEnabled: boolean;
}

/**
 * A 429 from `complete` may come from the code limiters or from the accounts-
 * per-network limit and the response does not say which. When the submission
 * carried a code the code text is the better guess; otherwise the retry
 * timing is shown, in minutes past a minute (rounded up, never down: the
 * student should never be told to wait less than the server will make them).
 */
function rateLimitedMessage(error: unknown, sentCode: boolean, t: TFunction): string {
  if (sentCode) return t("auth.code.rateLimited");
  const seconds = rateLimitRetrySeconds(error);
  if (seconds === null) return messageForError(error, t);
  if (seconds < 60) return t("auth.rateLimitedRetry.seconds", { seconds });
  const minutes = Math.ceil(seconds / 60);
  return t(
    minutes === 1 ? "auth.rateLimitedRetry.minutes.one" : "auth.rateLimitedRetry.minutes.other",
    { minutes },
  );
}

function isExpired(error: unknown): boolean {
  return isCode(error, "NOT_FOUND") || isCode(error, "UNAUTHENTICATED");
}

/**
 * Where a new Google identity lands (spec §6.1): the address comes from the
 * server (`/pending`), never from the URL, and nothing on the page is built
 * from the query string.
 */
export function RegisterGooglePage() {
  const t = useT();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const pending = useQuery({
    queryKey: ["auth", "google", "pending"],
    queryFn: () => api.get<PendingSignup>("/api/auth/google/pending"),
    retry: false,
    gcTime: 0,
  });

  const [displayName, setDisplayName] = useState("");
  const [registrationCode, setRegistrationCode] = useState("");
  const [teacherCode, setTeacherCode] = useState("");
  const [teacherOpen, setTeacherOpen] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [sentCode, setSentCode] = useState(false);
  const [busy, setBusy] = useState(false);
  const [restarting, setRestarting] = useState(false);

  const fields = fieldErrors(error, t);
  const fieldShown = Boolean(fields.displayName || fields.registrationCode || fields.teacherCode);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!pending.data) return;
    const body: { displayName: string; registrationCode?: string; teacherCode?: string } = {
      displayName: displayName.trim(),
    };
    if (pending.data.registrationCodeRequired && registrationCode.trim()) {
      body.registrationCode = registrationCode.trim();
    }
    // A collapsed section is not part of the form: its code is not sent.
    if (pending.data.teacherCodeEnabled && teacherOpen && teacherCode.trim()) {
      body.teacherCode = teacherCode.trim();
    }
    setBusy(true);
    setError(null);
    setSentCode(body.registrationCode !== undefined || body.teacherCode !== undefined);
    try {
      const { user, returnTo } = await api.post<{ user: SessionUser; returnTo?: string }>(
        "/api/auth/google/complete",
        body,
      );
      queryClient.setQueryData(ME_QUERY_KEY, user);
      // Not `/pending`: the server has just cleared the cookie, so a refetch
      // would answer 404 and flash the "expired" text before the navigation.
      await queryClient.invalidateQueries({
        predicate: (query) =>
          !(
            query.queryKey[0] === "auth" &&
            query.queryKey[1] === "google" &&
            query.queryKey[2] === "pending"
          ),
      });
      navigate(safeReturnTo(returnTo), { replace: true });
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  async function startOver() {
    setRestarting(true);
    try {
      await api.delete("/api/auth/google/pending");
    } catch {
      // Leaving matters more than the cookie being cleared now: the server
      // drops it after 15 minutes and every new sign-in clears it first.
    }
    navigate(authLink("login", safeReturnTo(pending.data?.returnTo)), { replace: true });
  }

  function errorText(): string {
    if (isCode(error, "EMAIL_TAKEN")) return t("auth.google.conflict");
    if (isCode(error, "RATE_LIMITED")) return rateLimitedMessage(error, sentCode, t);
    return messageForError(error, t);
  }

  const expired = isExpired(error) || (pending.isError && isExpired(pending.error));

  let content;
  if (expired) {
    content = (
      <div className="flex flex-col gap-4">
        <p role="alert" className="text-sm text-ink-800 dark:text-ink-100">
          {t("auth.google.pendingExpired")}
        </p>
        <Link to="/login" className="text-sm text-accent-700 underline dark:text-accent-300">
          {t("auth.signIn")}
        </Link>
      </div>
    );
  } else if (pending.isError) {
    content = (
      <div className="flex flex-col gap-4">
        <p role="alert" className="text-sm text-red-700 dark:text-red-300">
          {messageForError(pending.error, t)}
        </p>
        <Link to="/login" className="text-sm text-accent-700 underline dark:text-accent-300">
          {t("auth.signIn")}
        </Link>
      </div>
    );
  } else if (!pending.data) {
    content = (
      <div className="flex justify-center py-2">
        <Spinner label={t("common.loading")} />
      </div>
    );
  } else {
    const { email, registrationCodeRequired, teacherCodeEnabled } = pending.data;
    content = (
      <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        <p className="text-sm text-ink-700 dark:text-ink-300">
          {t("auth.google.completeAs", { email })}
        </p>
        <Input
          label={t("auth.displayName")}
          name="displayName"
          autoComplete="name"
          required
          value={displayName}
          error={fields.displayName}
          onChange={(event) => setDisplayName(event.target.value)}
        />
        {registrationCodeRequired && (
          <Input
            label={t("auth.registrationCode")}
            name="registrationCode"
            autoComplete="off"
            required={!(teacherCodeEnabled && teacherOpen)}
            value={registrationCode}
            error={fields.registrationCode}
            onChange={(event) => setRegistrationCode(event.target.value)}
          />
        )}
        {teacherCodeEnabled && (
          <div className="flex flex-col gap-3">
            <button
              type="button"
              aria-expanded={teacherOpen}
              onClick={() => setTeacherOpen((open) => !open)}
              className="self-start text-sm text-accent-700 underline dark:text-accent-300"
            >
              {t("auth.google.iAmTeacher")}
            </button>
            {teacherOpen && (
              <Input
                label={t("auth.teacherCode")}
                name="teacherCode"
                autoComplete="off"
                value={teacherCode}
                error={fields.teacherCode}
                onChange={(event) => setTeacherCode(event.target.value)}
              />
            )}
          </div>
        )}
        {error != null && !fieldShown && (
          <p role="alert" className="text-sm text-red-700 dark:text-red-300">
            {errorText()}
          </p>
        )}
        <Button type="submit" loading={busy} fullWidth>
          {t("auth.signUp")}
        </Button>
        <Button variant="ghost" loading={restarting} onClick={() => void startOver()} fullWidth>
          {t("auth.google.notYou")}
        </Button>
      </form>
    );
  }

  return (
    <main className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-4 py-12">
      <h1 className="mb-6 text-center font-display text-2xl text-ink-900 dark:text-ink-50">
        {t("auth.google.completeTitle")}
      </h1>
      <Card>{content}</Card>

      <section className="mt-6 rounded-xl border border-ink-200 bg-white p-4 text-sm dark:border-ink-800 dark:bg-ink-900">
        <h2 className="font-medium text-ink-800 dark:text-ink-100">{t("auth.privacyTitle")}</h2>
        <p className="mt-1 text-ink-600 dark:text-ink-400">{t("auth.privacyBody")}</p>
      </section>
      <Link
        to="/integritet"
        className="mt-4 text-center text-sm text-accent-700 underline dark:text-accent-300"
      >
        {t("privacy.link")}
      </Link>
    </main>
  );
}

export default RegisterGooglePage;
